import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDoubleDownIcon,
} from '@heroicons/react/20/solid'
import { Cell, Column, flexRender, Row, Table } from '@tanstack/react-table'
import { cn } from 'app/lib/utils'
import { OpenInWindow } from 'iconoir-react'
import { ReactElement, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Info } from '../Info'
import { Tooltip } from 'app/components/Tooltip'
import {
  TABLE_FRAME,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table'
import { measureAutoFitWidth } from 'app/lib/autoFitColumn'
import { closesRowGroup } from 'lib/closesRowGroup'
import { debug } from 'lib/logging'
import { CONTACT_PHI_FIELDS } from 'lib/phiFields'

function RenderCell<T>({
  cell,
  i,
  tooltipContent,
  onSetTooltipContent,
  isInPinnedRow,
  cellClassName,
}: {
  cell: Cell<T, unknown>
  i: number
  tooltipContent?: { cellId: string; content: ReactElement | ReactNode }
  onSetTooltipContent: (content?: { cellId: string; content: ReactElement | ReactNode }) => void
  isInPinnedRow?: boolean
  cellClassName?: string
}) {
  const cellTooltipContent = tooltipContent?.cellId === cell.id ? tooltipContent.content : undefined

  return (
    <TableCell
      key={cell.id ?? i}
      data-column-id={cell.column.id}
      className={cn(
        `group/cell h-12 border-b border-r border-default bg-surface p-0 text-left text-body-xs text-default last:border-r-0`,
        i === 0 && !isInPinnedRow && `md:sticky! md:left-0 md:z-10`,
        cell.column.getIsPinned() === `right` && `border-l md:sticky! md:right-0 md:z-10`,
        cell.column.columnDef.meta?.getCellStyle?.(cell.getValue()) ?? ``,
        CONTACT_PHI_FIELDS.has(cell.column.id) && `phi-wrapper`,
        cellClassName,
      )}
    >
      <>
        {cell?.column?.columnDef?.meta?.centered ? (
          <div className="flex h-full items-center justify-center">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        ) : cell?.column?.columnDef?.meta?.ControlledComponent ? (
          cell?.column?.columnDef?.meta?.ControlledComponent?.(cell.row.original, cell.column, cell.row.index)
        ) : (
          <div className="flex items-center justify-between px-2 text-body-xs">
            <Tooltip
              className={cn(
                `overflow-hidden overflow-ellipsis whitespace-nowrap`,
                cell.column.columnDef.meta?.tooltipClassName,
                cell.column.columnDef.meta?.onClick && `cursor-pointer!`,
              )}
              placement="bottom"
              message={cellTooltipContent}
              onMouseOver={(event) => {
                const target = event.currentTarget
                const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
                onSetTooltipContent(
                  isOverflowing
                    ? {
                        cellId: cell.id,
                        content:
                          cell?.column?.columnDef?.meta?.tooltipText?.(cell.row.original) ??
                          flexRender(cell.column.columnDef.cell, cell.getContext()),
                      }
                    : undefined,
                )
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Tooltip>
            {!!cell.column.columnDef.meta && !!cell.column.columnDef.meta?.onClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  cell.column.columnDef.meta!.onClick!(cell.row.original)
                }}
                className="mr-2 hidden h-8 items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong hover:bg-surface-muted group-hover:flex"
              >
                <OpenInWindow height={16} width={16} className="text-strong" />
              </button>
            )}
            {!!cell.column.columnDef.meta && !!cell.column.columnDef.meta?.onOpen && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  cell.column.columnDef.meta!.onOpen!(cell.row.original)
                }}
                className="mr-2 hidden h-8 items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong group-hover:flex"
              >
                <p>Open</p>
                <ChevronRightIcon height={16} width={16} />
              </button>
            )}
          </div>
        )}
      </>
    </TableCell>
  )
}

function PinnedRow({
  row,
  table,
  tooltipContent,
  onSetTooltipContent,
}: {
  row: Row<any>
  table: Table<any>
  tooltipContent?: { cellId: string; content: ReactElement | ReactNode }
  onSetTooltipContent: (content?: { cellId: string; content: ReactElement | ReactNode }) => void
}) {
  return (
    <TableRow
      style={{
        position: `sticky`,
        top: row.getIsPinned() === `top` ? `${row.getPinnedIndex() * 26 + 48}px` : undefined,
        bottom:
          row.getIsPinned() === `bottom`
            ? `${(table.getBottomRows().length - 1 - row.getPinnedIndex()) * 26}px`
            : undefined,
      }}
      className={cn(
        table.options.meta?.getRowStyle?.(row.original, row.depth),
        `group z-20 h-8 border-b border-default`,
        table.options.meta?.onRowClick && `cursor-pointer`,
      )}
      onClick={() => table.options.meta?.onRowClick?.(row.original)}
    >
      {row.getVisibleCells().map((cell, i) => (
        <RenderCell
          key={cell.id}
          cell={cell}
          i={i}
          tooltipContent={tooltipContent}
          onSetTooltipContent={onSetTooltipContent}
          isInPinnedRow
        />
      ))}
    </TableRow>
  )
}

export function DataTable<T>({
  table,
  className,
  toggleSort,
  sortOrder,
  showFooter = false,
  onScroll,
  wrapperClassName,
  enableHoverStateStyling = true,
  onContainerResize,
  onAutoFitColumn,
  emptyState,
}: {
  table: Table<T>
  className?: string
  toggleSort: (column: Column<T, unknown>) => void
  sortOrder?: { columnId?: string; order?: `asc` | `desc` }
  showFooter?: boolean
  onScroll?: React.UIEventHandler<HTMLDivElement>
  wrapperClassName?: string
  enableHoverStateStyling?: boolean
  onContainerResize?: (width: number) => void
  /**
   * Commits a double-click auto-fit. Column widths only persist when the owning
   * hook treats the change as a finished resize, so without this the fit applies
   * and then disappears on reload.
   */
  onAutoFitColumn?: (columnId: string, width: number) => void
  /** Rendered in a full-width row when there are no center rows. */
  emptyState?: ReactNode
}) {
  const [headerTooltipContent, setHeaderTooltipContent] = useState<ReactElement | ReactNode>(undefined)
  const [tooltipContent, setTooltipContent] = useState<{ cellId: string; content: ReactElement | ReactNode }>()
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const previousWidthRef = useRef<number>(0)

  const rows = table.getCenterRows()
  const isEmpty = rows.length === 0
  // Visible width of the scroll container, used to size the empty row so it
  // centers on what the reader can see rather than on the table's full width.
  const [emptyRowWidth, setEmptyRowWidth] = useState<number>()
  // Virtualizer measurements change outside React; its callbacks must stay fresh.
  // oxlint-disable-next-line react/incompatible-library
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: rows.length,
    overscan: 10,
    estimateSize: useCallback(() => 48, []),
  })
  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  const paddingTop = isEmpty || virtualRows.length === 0 ? 0 : virtualRows?.[0]?.start || 0
  const paddingBottom =
    isEmpty || virtualRows.length === 0 ? 0 : totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)

  useEffect(() => {
    debug(`virtual`, `virtualRows`, virtualRows.length, totalSize)
  }, [virtualRows, totalSize])

  useLayoutEffect(() => {
    const element = tableContainerRef.current
    if (!element || !isEmpty) return

    const measure = () => setEmptyRowWidth(element.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [isEmpty])

  useEffect(() => {
    const element = tableContainerRef.current
    if (!element) return

    let debounceTimeout: ReturnType<typeof setTimeout> | null = null
    let cooldownUntil = 0

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return

      const newWidth = entry.contentRect.width
      if (!newWidth) return

      if (!onContainerResize) return

      const now = Date.now()
      if (now < cooldownUntil) return

      const widthDiff = Math.abs(newWidth - previousWidthRef.current)
      if (widthDiff < 10) return

      previousWidthRef.current = newWidth

      if (debounceTimeout) clearTimeout(debounceTimeout)
      debounceTimeout = setTimeout(() => {
        cooldownUntil = Date.now() + 300
        onContainerResize(newWidth)
      }, 150)
    })

    resizeObserver.observe(element)

    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout)
      resizeObserver.disconnect()
    }
  }, [onContainerResize])

  return (
    // The scroll container is also the frame: `overflow-auto` clips painted
    // content to the radius, so the grid's corners round without touching the
    // corner cells, and `position: sticky` still works for the header and the
    // pinned first column because this element owns the scroll.
    //
    // `max-h-full`, not `h-full`: a short table closes its frame under the last
    // row instead of stretching an empty card to the bottom of the page. Once
    // the rows outgrow the space the cap takes over and this element scrolls
    // exactly as before (the virtualizer's padding rows keep the content height
    // equal to the full row count either way).
    <div
      x-component="DataTable-scrolldiv"
      className={cn(`relative max-h-full w-full overflow-auto`, TABLE_FRAME, wrapperClassName)}
      onScroll={onScroll}
      ref={tableContainerRef}
    >
      <table
        className={cn(
          `table-component-table relative w-full table-fixed border-separate border-spacing-0 divide-y divide-strong overflow-clip border-l-0 border-t-0`,
          className,
          enableHoverStateStyling && `table-hover-styling`,
          // Lets the single empty-state row stretch to fill the frame (via
          // TableCell's `align-middle`) instead of collapsing to `auto`.
          // `min-h-full` rather than `h-full` so the table can still grow past
          // the frame rather than being pinned to exactly its height; where an
          // ancestor has no definite height the percentage resolves to `auto`
          // and TableEmptyState's own `min-h-70` is the floor. Only applied when
          // empty, so populated tables keep their existing height model.
          isEmpty && `min-h-full`,
        )}
      >
        <colgroup>
          {table
            .getHeaderGroups()
            .map((headerGroup) =>
              headerGroup.headers?.map((header) => <col key={header.id} style={{ width: header.getSize() }} />),
            )}
        </colgroup>
        <TableHeader style={{ insetBlockStart: 0 }} className="border-t-0! border-collapse">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-collapse border-t-0">
              {headerGroup.headers?.map((header, i) => (
                <TableHead
                  key={header.id}
                  scope="col"
                  data-column-id={header.column.id}
                  className={cn(
                    `sticky top-0 z-10 h-12 border-collapse border-b border-r border-default bg-surface-subtle px-2 py-0 text-left text-ui-xs font-bold text-default last:border-r-0`,
                    i === 0 && `md:left-0 md:z-11!`,
                    header.column.getIsPinned() === `right` && `border-l md:right-0 md:z-11!`,
                  )}
                >
                  {header.column.columnDef.meta?.centered ? (
                    // th keeps its left padding but shadcn strips right padding on checkbox
                    // cells; cancel the left to truly center the checkbox in the column.
                    <div className="-ml-2 flex h-full items-center justify-center">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center">
                        <Tooltip
                          key={header.id}
                          className="overflow-hidden overflow-ellipsis whitespace-nowrap"
                          placement="bottom"
                          message={headerTooltipContent}
                          onMouseOver={(event) => {
                            if (
                              event.currentTarget instanceof HTMLElement &&
                              event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                            ) {
                              setHeaderTooltipContent(
                                header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext()),
                              )
                            } else {
                              setHeaderTooltipContent(undefined)
                            }
                          }}
                        >
                          <span className="flex flex-initial items-center overflow-hidden text-ellipsis whitespace-nowrap text-ui-xs">
                            {i === 0 && table.getCanSomeRowsExpand() && (
                              <ChevronDoubleDownIcon
                                onClick={() => table.toggleAllRowsExpanded()}
                                className={cn(
                                  `mr-1 h-4 w-4 cursor-pointer`,
                                  table.getIsAllRowsExpanded() && `rotate-180`,
                                )}
                              />
                            )}
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        </Tooltip>
                        {!!header.column.columnDef.meta?.infoTooltip && (
                          <Info
                            title={header.column.columnDef.meta?.infoTooltip?.title}
                            message={header.column.columnDef.meta.infoTooltip.message}
                            className="ml-2"
                          />
                        )}
                        <span className="ml-auto whitespace-normal">
                          {header.column.getCanSort() && (
                            <div className="inline cursor-pointer" onClick={() => toggleSort(header.column)}>
                              {(sortOrder && sortOrder.columnId === header.column.id && sortOrder.order === `asc`) ||
                              header.column.getIsSorted() === `asc` ? (
                                <ChevronUpIcon className="ml-3 inline h-4 w-4 fill-current text-default" />
                              ) : (sortOrder &&
                                  sortOrder.columnId === header.column.id &&
                                  sortOrder.order === `desc`) ||
                                header.column.getIsSorted() === `desc` ? (
                                <ChevronDownIcon className="ml-3 inline h-4 w-4 fill-current text-default" />
                              ) : (sortOrder && (header.column.id !== sortOrder.columnId || !sortOrder.order)) ||
                                !header.column.getIsSorted() ? (
                                <ChevronUpDownIcon className="ml-3 inline h-4 w-4 fill-current text-default" />
                              ) : null}
                            </div>
                          )}
                        </span>
                      </div>
                      {header.column.getCanResize() && (
                        <div
                          className={cn(`resizer`, `${header.column.getIsResizing() ? `isResizing` : ``}`)}
                          onMouseDown={header.getResizeHandler()}
                          // Excel's double-click-to-fit. A double-click moves the
                          // pointer nowhere, so the drag handler above resolves to a
                          // zero-width change and the two don't fight.
                          onDoubleClick={() => {
                            const container = tableContainerRef.current
                            if (!container) return
                            const width = measureAutoFitWidth(table, header.column.id, container)
                            if (!width) return
                            if (onAutoFitColumn) onAutoFitColumn(header.column.id, width)
                            else table.setColumnSizing((prev) => ({ ...prev, [header.column.id]: width }))
                          }}
                        />
                      )}
                    </>
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-surface">
          {paddingTop > 0 && (
            <TableRow>
              <TableCell style={{ height: `${paddingTop}px` }} />
            </TableRow>
          )}
          {table.getTopRows().map((row) => (
            <PinnedRow
              key={row.id}
              row={row}
              table={table}
              tooltipContent={tooltipContent}
              onSetTooltipContent={setTooltipContent}
            />
          ))}
          {isEmpty && emptyState != null && (
            // Rendered after pinned rows (`getTopRows`) per the design spec: pinned
            // rows stay visible above the empty state rather than being hidden
            // beneath it. One cell spanning every column; it deliberately skips
            // RenderCell, whose sticky-first-column and pinned-right classes would
            // detach this cell from its row inside `table-fixed`.
            // `data-slot` marks this as chrome rather than data. An empty table
            // used to render no rows at all, so anything counting `tbody tr` read
            // zero; it now finds this one. Callers that mean "how many records"
            // exclude it by this attribute.
            <TableRow data-slot="table-empty-row">
              {/* `whitespace-normal` undoes TableCell's own `whitespace-nowrap`,
                  which every descendant inherits. Without it the description
                  cannot wrap: its box respects `max-w-md` while the text runs
                  straight out of it in one long line.

                  Nothing here cancels the row hover: the global
                  `table.table-hover-styling` rule in tailwind.css excludes this
                  row by its data-slot instead. */}
              <TableCell colSpan={table.getVisibleFlatColumns().length} className="h-full whitespace-normal p-0">
                {/* The cell is as wide as the whole table, which on a table wide
                    enough to scroll sideways would center the empty state on the
                    scrollable width instead of the part the reader can see. Pin
                    it to the viewport edge at the scroll container's own width so
                    it stays centered in view however far the table scrolls.
                    `w-full` is the fallback for the frame before it is measured,
                    and for a container whose clientWidth reads 0 because it is
                    collapsed or off-screen. */}
                <div
                  className="sticky left-0 h-full w-full"
                  style={emptyRowWidth ? { width: emptyRowWidth } : undefined}
                >
                  {emptyState}
                </div>
              </TableCell>
            </TableRow>
          )}
          {!!rows &&
            virtualRows?.map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<T>
              if (!row) return <>No row</>
              return (
                <TableRow
                  // Not the whole row: TanStack fills its `_valuesCache` during render, so that key never holds.
                  key={`${row.id}:${JSON.stringify(row.original)}`}
                  className={cn(
                    table.options.meta?.getRowStyle?.(row.original, row.depth),
                    `group h-8 border-b border-default`,
                    table.options.meta?.onRowClick && `cursor-pointer`,
                  )}
                  onClick={() => table.options.meta?.onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell, i) => (
                    <RenderCell
                      key={cell.id}
                      cell={cell}
                      i={i}
                      tooltipContent={tooltipContent}
                      onSetTooltipContent={setTooltipContent}
                      cellClassName={closesRowGroup(row, table) ? `border-b-2!` : undefined}
                    />
                  ))}
                </TableRow>
              )
            })}
          {paddingBottom > 0 && (
            <TableRow>
              <TableCell style={{ height: `${paddingBottom}px` }} />
            </TableRow>
          )}
        </TableBody>
        {showFooter ? (
          <TableFooter className="sticky z-10 bg-surface-muted" style={{ insetBlockEnd: 0 }}>
            {table.getFooterGroups().map((footerGroup) => (
              <TableRow key={footerGroup.id}>
                {footerGroup.headers?.map((header, i) => (
                  <TableHead
                    key={header.id}
                    scope="col"
                    className={cn(
                      `sticky top-0 z-10 h-12 border-r border-default bg-surface-muted px-2 text-left text-ui-xs text-default last:border-r-0`,
                      i === 0 && `md:left-0 md:z-20!`,
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="overflow-hidden overflow-ellipsis whitespace-nowrap text-ui-xs">
                        {flexRender(header.column.columnDef.footer, header.getContext())}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableFooter>
        ) : null}
      </table>
    </div>
  )
}
